import {
  ChangeEmailError,
  Mutation,
  Typenames,
} from '@varo/graphql-schema-types';
import { MyVaroContext } from 'features/myVaro/provider/MyVaroContextProvider';
import { Dispatch, SetStateAction, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChangeEmailResendLink from 'services/graphql/mutations/ChangeEmailResendLink';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useMutation } from 'urql';

interface ConfirmEmail {
  fetching: boolean;
  errorScreen: boolean | undefined;
  resendLinkOnEmail: () => Promise<void>;
  isDialogVisible: boolean;
  resendCount: number;
  setIsDialogVisible: Dispatch<SetStateAction<boolean>>;
}

const useConfirmEmail = (): ConfirmEmail => {
  const [{ fetching }, executeMutation] = useMutation<Mutation>(
    ChangeEmailResendLink,
  );

  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'confirmEmail',
  });

  const { setSnackBarMessage } = useContext(MyVaroContext);

  const [errorScreen, setErrorScreen] = useState<boolean | undefined>();
  const [isDialogVisible, setIsDialogVisible] = useState<boolean>(false);
  const [resendCount, setResendCount] = useState<number>(1);

  const logLimitExceeded = usePressableAnalytics({
    analyticsId: 'emailOtpLimitExceeded',
    customAttributes: {
      name: 'Email OTP Attempt Limit Exceeded',
    },
  });

  const resendLinkOnEmail = async () => {
    try {
      setResendCount(resendCount + 1);
      const response = await executeMutation();
      const responseStatus = response?.data?.changeEmailResendLink.__typename;
      if (responseStatus === Typenames.ChangeEmailResendLinkSuccess) {
        setSnackBarMessage?.(translatedText('message.sentLinkToMail'));
        return;
      } else if (responseStatus === Typenames.ChangeEmailTooManyAttempts) {
        logLimitExceeded();
        setIsDialogVisible(true);
        return;
      }
      const respMessage = response?.data
        ?.changeEmailResendLink as ChangeEmailError;
      setSnackBarMessage?.(respMessage.message);
    } catch {
      setErrorScreen(true);
      // TODO - add analytics - JT-121593
    }
  };

  return {
    fetching,
    errorScreen,
    resendCount,
    resendLinkOnEmail,
    isDialogVisible,
    setIsDialogVisible,
  };
};

export default useConfirmEmail;
